/**
 * running-algorithm.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Observable, Observer } from "rxjs/Rx";

import { Variable } from "../../aggregation/typed-parameter";
import { MessageSender } from "../../server/message-sender";
import {
    LoadAlgorithmRequest,
    StartAlgorithmRequest,
    StopAlgorithmRequest
} from "../../server/messages/algorithms/";
import { SaveSubjectContextRequest }
    from "../../server/messages/context-management";
import { AcknowledgmentResponse, ResponseCode }
    from "../../server/messages/general";
import { Subject } from "../../subjects/subject";

import { StepManager } from "../management/pipeline/step-manager";
import { TransformerService } from "../management/pipeline/transformer.service";
import { Algorithm, AlgorithmType } from "./algorithm";

const UI_LAUNCH_WINDOW : number = 4 * 1000;

/**
 * A RunningAlgorithm represents the combined resource of an {@link Algorithm}
 * and a {@link Subject} as loaded into an agent of the server rules engine.
 * Effectively, it represents one continuous "execution" of the algorithm, or an
 * open "session" of algorithm usage (with regard to a particular target subject
 * context).
 *
 * Note that the actual lifecycle of the client object extends before and after
 * the protocol ALGORITHM_RUNNING state, so despite the current namesake, the
 * object's existence does not guarantee that it is currently running (check
 * this.isRunning). However, an instance's presence as the referent of
 * {@link AlgorithmService}.running will always be accurate (relative to the
 * client's knowledge).
 */
export class RunningAlgorithm
{
    /**
     * The {@link AlgorithmStep}s known by the client, as held by a
     * {@link StepManager}.
     */
    public steps : StepManager = new StepManager(this.transformer);

    /**
     * Whether the algorithm IS actively running.
     *
     * (false usually indicates that the object was just created and is awaiting
     * server request or that it was shutdown and is awaiting GC / release of
     * the local JS object)
     */
    public isRunning : boolean = false;

    /**
     * Whether this algorithm started "recently" (relative to user timescales)
     * Helps the UI suppress async updates received during the initial load
     * window that are naturally expected to occur, but can't be sent
     * synchronously due to protocol and parallelization constraints.
     */
    public recentlyLaunched : boolean = true;

    /**
     * Whether the algorithm *may* have additional requestable steps. (DISUSED)
     *
     * This function was used by an alternative rules engine agent that didn't
     * eagerly retrieve all activated {@link AlgorithmStep}s and required UI
     * interaction to request them. (see {@link AdditionalStepsRequest}) The
     * model was abandoned for the time being.
     */
    public mayHaveUnexploredSteps : boolean = false;

    /**
     * Timestamp of the last save notification from a collaborator.
     */
    public lastExternalSave : Date = new Date(0);

    /**
     * Timestamp of the last successful save requested by the current user.
     */
    public lastInternalSave : Date = new Date(0);

    /**
     * Shortcut for accessing the type of the underlying {@link Algorithm}
     */
    public get type () : AlgorithmType
    {
        return this.algorithm.type;
    }

    /**
     * Shortcut for accessing the name of the underlying {@link Algorithm}
     */
    public get name () : string
    {
        return this.algorithm.name;
    }

    /**
     * Shortcut for accessing the label of the underlying {@link Algorithm}
     */
    public get displayName () : string
    {
        return this.algorithm.displayName;
    }

    constructor (
        public algorithm : Algorithm,
        public subject : Subject,
        protected transformer : TransformerService,
        protected sender : MessageSender
    )
    {
        // Ensure that the subject has acquired a type by the time we attempt to
        // construct a running algorithm.
        subject.assertTypeDefined();
    }

    /**
     * Request the algorithm to stop.
     */
    public stop () : Observable<true>
    {
        if (this.isRunning)
        {
            return this.sender.successful(new StopAlgorithmRequest());
        }
        else
        {
            console.warn("Request to stop non-running algorithm");
            return Observable.empty<true>();
        }
    }

    /**
     * Request that the algorithm enters a running state.
     */
    public start () : Observable<true>
    {
        // The algorithm functions are designed to permit trivial requests,
        // so if the algorithm IS running, simply return an empty response.
        if (this.isRunning)
        {
            console.info("Request to start already-running algorithm");
            return Observable.empty<true>();
        }
        else
        {
            if (!this.subject.loaded)
            {
                // Auto-loading the subject gets too challenging to manage
                // intent for (at the very least, it was a sticking point for
                // the previous design), so instead fail hard, as this usage
                // is entire unintended and shouldn't be triggered due to the
                // guard setup.
                console.error(this);
                throw new Error("Subject not loaded at algorithm start!");
            }
            return Observable.concat(
                this.load(),
                // this.subject.load(),
                this.launch()
            ).last().do(() => this.isRunning = true);
        }
    }

    /**
     * Attempt to load an algorithm if it hasn't been loaded.
     */
    private load () : Observable<true>
    {
        if (this.algorithm.loadedThisSession)
        {
            // If it's already been loaded, there's no reason to request again.
            return Observable.empty<true>();
        }
        return this.sender.successful(
            new LoadAlgorithmRequest(this.algorithm.name)).do(
                () => this.algorithm.loadedThisSession = true);
    }

    /**
     * Request to enter the *running* state for a particular algorithm.
     */
    private launch () : Observable<true>
    {
        return this.sender.successful(new StartAlgorithmRequest(
            this.algorithm.name, this.subject.id)
        ).do(
            () => setTimeout(
                () => this.recentlyLaunched = false, UI_LAUNCH_WINDOW));
    }

    /**
     * Request to save the context this algorithm is running.
     * @param force Whether to ignore indexing suggestions
     */
    public save (force : boolean = false) : Observable<ResponseCode>
    {
        return this.sender.send(
            new SaveSubjectContextRequest(this.subject.id, force)
        ).map((msg : AcknowledgmentResponse) =>
            (msg.ok ? ResponseCode.OK : msg.code)
        ).do((code : ResponseCode) => {
            if (code === ResponseCode.OK)
            {
                this.lastInternalSave = new Date();
                this.subject.newlyCreated = false;
            }
        });
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/structure/running-algorithm.ts